import styled from 'styled-components'
import { background, BackgroundProps } from 'styled-system'
import { themeGet } from '@styled-system/theme-get'

import { LANDING_MAX_CONTENT_WIDTH_PX } from 'Constants/Landing'

export const SectionWrapper = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${LANDING_MAX_CONTENT_WIDTH_PX}px;
  padding: 120px 16px;
  background: linear-gradient(
    to bottom,
    ${themeGet('colors.surface.block')},
    transparent
  );

  @media screen and (min-width: ${themeGet('breakpoints.1')}) {
    padding: 120px 40px;
  }
`

export const Strong = styled.strong`
  font-weight: 500;
`

export const CasesGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  justify-items: center;
  gap: 16px;
  width: 100%;

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    grid-template-columns: repeat(2, 1fr);
  }
`
export const CaseCard = styled.div<BackgroundProps>`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    'content'
    'image';
  border-radius: 24px;
  overflow: hidden;
  max-width: calc(min(520px, 100%));

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    grid-template-columns: 1fr 1fr;
    grid-template-areas: 'content image';
    align-items: center;
  }

  ${background}
`

export const CaseImage = styled.div`
  grid-area: image;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  > img {
    display: block;
    width: 100%;
    max-width: 400px;
    height: auto;
    object-fit: cover;
    object-position: left top;
  }

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    max-height: 100%;

    > img {
      object-position: left center;
    }
  }
`

export const CaseContent = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-width: 0;
  padding: 32px;
`

export const CaseLogo = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 40px;
  height: 40px;

  > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`
export const CheckList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${themeGet('space.3')}px;
  min-width: 0;
`

export const CheckItem = styled.li`
  display: flex;
  gap: ${themeGet('space.4')}px;
`

export const CheckIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  color: ${themeGet('colors.success.default')};

  > svg {
    width: 100%;
    height: 100%;
  }
`

export const LearnMoreLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${themeGet('space.3')}px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 500;
  color: ${themeGet('colors.text.link')};
  text-decoration: none;
  width: fit-content;

  &:hover {
    text-decoration: underline;
    color: ${themeGet('colors.text.linkHover')};
  }
`

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  position: relative;
  width: 100%;
  margin-top: 80px;

  &::before {
    content: '';
    display: none;
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-repeat: no-repeat;
  }

  @media screen and (min-width: ${themeGet('breakpoints.2')}) {
    grid-template-columns: repeat(2, 1fr);

    // NOTE: It's a bit tricky to draw divider line with fading effect without changing background,
    //       which we can't change because the section uses a gradient background
    &::before {
      display: block;
      background-image: linear-gradient(
          to bottom,
          transparent,
          ${themeGet('colors.border.slight')} 14%,
          ${themeGet('colors.border.slight')} 86%,
          transparent
        ),
        linear-gradient(
          to right,
          transparent,
          ${themeGet('colors.border.slight')} 14%,
          ${themeGet('colors.border.slight')} 86%,
          transparent
        ),
        linear-gradient(
          to right,
          transparent,
          ${themeGet('colors.border.slight')} 14%,
          ${themeGet('colors.border.slight')} 86%,
          transparent
        );
      background-size: 1px calc(100% - 48px), calc(100% - 48px) 1px,
        calc(100% - 48px) 1px;
      background-position: center center, center calc(100% / 3),
        center calc((100% / 3) * 2);
    }
  }

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    grid-template-columns: repeat(3, 1fr);

    &::before {
      background-image: linear-gradient(
          to bottom,
          transparent,
          ${themeGet('colors.border.slight')} 14%,
          ${themeGet('colors.border.slight')} 86%,
          transparent
        ),
        linear-gradient(
          to bottom,
          transparent,
          ${themeGet('colors.border.slight')} 14%,
          ${themeGet('colors.border.slight')} 86%,
          transparent
        ),
        linear-gradient(
          to right,
          transparent,
          ${themeGet('colors.border.slight')} 14%,
          ${themeGet('colors.border.slight')} 86%,
          transparent
        );
      background-size: 1px calc(100% - 48px), 1px calc(100% - 48px),
        calc(100% - 48px) 1px;
      background-position: calc(100% / 3) center, calc((100% / 3) * 2) center,
        center center;
    }
  }
`

export const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 24px;
  text-align: center;
  position: relative;

  &::before {
    content: '';
    left: 24px;
    right: 24px;
    bottom: 0;
    position: absolute;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      ${themeGet('colors.border.slight')} 14%,
      ${themeGet('colors.border.slight')} 86%,
      transparent
    );
  }

  &:last-child::before {
    display: none;
  }

  @media screen and (min-width: ${themeGet('breakpoints.2')}) {
    &::before {
      display: none;
    }
  }

  @media screen and (min-width: ${themeGet('breakpoints.3')}) {
    &::before {
      display: none;
    }
  }
`

export const StatValue = styled.p`
  margin: 0;
  padding: 0;
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  color: ${themeGet('colors.text.title')};
  letter-spacing: 0.02em;
`

export const StatLabel = styled.p`
  margin: 0;
  padding: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${themeGet('colors.text.body')};
  letter-spacing: 0.03em;
`
